import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Modal } from '../../../components/ui/Modal';
import Chart from '../../../components/ui/Chart';
import {
  BarChart3, TrendingUp, Activity, Clock, CheckCircle, XCircle, AlertTriangle, RefreshCw, Download, Filter, Calendar, Brain, Zap, Target, Users, MessageSquare, Lightbulb, Settings
} from 'lucide-react';
import MockDataService from '../../../services/MockDataService';
import toast from 'react-hot-toast';

const AIAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [aiModels, setAiModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filters, setFilters] = useState({
    timeRange: '7d',
    modelType: 'all',
    status: 'all'
  });
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`;
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [analyticsResponse, modelsResponse] = await Promise.all([
          MockDataService.getAIAnalytics(),
          MockDataService.getAIModels()
        ]);

        if (analyticsResponse.success) {
          setAnalytics(analyticsResponse.data);
        }
        if (modelsResponse.success) {
          setAiModels(modelsResponse.data);
        }
      } catch (err) {
        setError("Failed to fetch AI analytics data.");
        console.error("Error fetching AI analytics:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    setRefreshInterval(interval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const response = await MockDataService.getAIAnalytics();
      if (response.success) {
        setAnalytics(response.data);
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        setLastUpdated(`${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`);
        toast.success('Analytics refreshed successfully');
      }
    } catch (err) {
      console.error("Error refreshing analytics:", err);
      toast.error('Failed to refresh analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      const response = await MockDataService.exportAIAnalytics(filters);
      if (response.success) {
        toast.success('AI analytics data exported successfully!');
      }
    } catch (err) {
      toast.error('Failed to export analytics data');
      console.error("Error exporting analytics:", err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'error': return 'destructive';
      case 'warning': return 'outline';
      default: return 'outline';
    }
  };

  const getPerformanceColor = (value, threshold) => {
    if (value >= threshold) return 'text-green-600';
    if (value >= threshold * 0.8) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getModelTypeIcon = (type) => {
    switch (type) {
      case 'chat': return <MessageSquare className="h-4 w-4" />;
      case 'classification': return <Target className="h-4 w-4" />;
      case 'generation': return <Lightbulb className="h-4 w-4" />;
      case 'analysis': return <BarChart3 className="h-4 w-4" />;
      default: return <Brain className="h-4 w-4" />;
    }
  };

  const filteredModels = aiModels.filter(model => {
    const matchesType = filters.modelType === 'all' || model.type === filters.modelType;
    const matchesStatus = filters.status === 'all' || model.status === filters.status;
    return matchesType && matchesStatus;
  });

  // Chart data for performance trends
  const performanceTrendsData = analytics ? [
    { name: 'Mon', value: analytics.totalRequests * 0.12 },
    { name: 'Tue', value: analytics.totalRequests * 0.15 },
    { name: 'Wed', value: analytics.totalRequests * 0.14 },
    { name: 'Thu', value: analytics.totalRequests * 0.18 },
    { name: 'Fri', value: analytics.totalRequests * 0.16 },
    { name: 'Sat', value: analytics.totalRequests * 0.13 },
    { name: 'Sun', value: analytics.totalRequests * 0.12 }
  ] : [];

  if (loading && !analytics) return <div className="p-6 text-center">Loading AI Analytics...</div>;
  if (error) return <div className="p-6 text-center text-red-500">Error: {error}</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Breadcrumb and Last Updated */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500">
          <span className="text-gray-700 font-medium">AI Orchestrator</span>
          <span className="mx-2">/</span>
          <span>AI Analytics</span>
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {lastUpdated}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Analytics</h1>
          <p className="text-gray-600 mt-1 text-sm">Monitor AI model performance and insights</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={handleRefresh} 
            disabled={loading}
            className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportData}
            className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Filter className="h-5 w-5 text-gray-700" />
            Filters & Time Range
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <select
                value={filters.timeRange}
                onChange={(e) => setFilters({...filters, timeRange: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="1d">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
            </div>

            <select
              value={filters.modelType}
              onChange={(e) => setFilters({...filters, modelType: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Model Types</option>
              <option value="chat">Chat Models</option>
              <option value="classification">Classification</option>
              <option value="generation">Generation</option>
              <option value="analysis">Analysis</option>
            </select>

            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
          {/* Total AI Requests Card */}
          <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Total AI Requests</p>
                  <p className="text-4xl font-extrabold text-gray-900 mb-2">{analytics.totalRequests.toLocaleString()}</p>
                  <p className={`text-xs font-medium ${
                    analytics.requestGrowth >= 10 ? 'text-green-600' : analytics.requestGrowth >= 5 ? 'text-violet-600' : 'text-gray-600'
                  }`}>
                    +{analytics.requestGrowth}% from last period
                  </p>
                </div>
                <div className="ml-4 p-3 bg-violet-400 rounded-xl shadow-md">
                  <Brain className="h-10 w-10 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Success Rate Card */}
          <Card className="bg-gradient-to-br from-emerald-50 to-green-100 border-2 border-green-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-700 mb-2 uppercase tracking-wide">Success Rate</p>
                  <p className="text-4xl font-extrabold text-green-900 mb-2">{analytics.successRate}%</p>
                  <p className={`text-xs font-medium ${
                    analytics.successRate >= 95 ? 'text-green-600' : analytics.successRate >= 85 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {analytics.successRate >= 95 ? 'Excellent' : analytics.successRate >= 85 ? 'Good' : 'Needs Attention'}
                  </p>
                </div>
                <div className="ml-4 p-3 bg-green-500 rounded-xl shadow-md">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Avg Response Time Card */}
          <Card className="bg-gradient-to-br from-pink-50 to-purple-100 border-2 border-pink-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-purple-700 mb-2 uppercase tracking-wide">Avg Response Time</p>
                  <p className="text-4xl font-extrabold text-purple-900 mb-2">{analytics.avgResponseTime}ms</p>
                  <p className={`text-xs font-medium ${
                    analytics.avgResponseTime < 1000 ? 'text-green-600' : analytics.avgResponseTime < 2000 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {analytics.avgResponseTime < 1000 ? 'Fast' : analytics.avgResponseTime < 2000 ? 'Good' : 'Slow'}
                  </p>
                </div>
                <div className="ml-4 p-3 bg-purple-500 rounded-xl shadow-md">
                  <Clock className="h-10 w-10 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Models Card */}
          <Card className="bg-gradient-to-br from-amber-50 to-orange-100 border-2 border-amber-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-orange-700 mb-2 uppercase tracking-wide">Active Models</p>
                  <p className="text-4xl font-extrabold text-orange-900 mb-2">{analytics.activeModels}</p>
                  <p className="text-xs font-medium text-orange-600">
                    {analytics.activeModels}/{analytics.totalModels} models active
                  </p>
                </div>
                <div className="ml-4 p-3 bg-orange-500 rounded-xl shadow-md">
                  <Zap className="h-10 w-10 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Performance Trends */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <Card className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                AI Performance Trends (7 days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {performanceTrendsData.length > 0 ? (
                <Chart
                  type="line"
                  data={performanceTrendsData}
                  height={300}
                  colors={['#3B82F6']}
                />
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <BarChart3 className="h-12 w-12" />
                  <span className="ml-2">No data available</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Activity className="h-5 w-5 text-green-600" />
                Model Health Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span>Overall Health Score:</span>
                <Badge variant={analytics.healthScore >= 90 ? 'success' : analytics.healthScore >= 70 ? 'secondary' : 'destructive'}>
                  {analytics.healthScore}/100
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span>Active Models:</span>
                <span>{analytics.activeModels}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Failed Requests:</span>
                <span className="text-red-600">{analytics.failedRequests}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Last Model Update:</span>
                <span>{new Date(analytics.lastUpdate).toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Models List */}
      <Card>
        <CardHeader>
          <CardTitle>AI Models Status ({filteredModels.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredModels.map((model) => (
              <div key={model.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getModelTypeIcon(model.type)}
                      <h3 className="text-lg font-semibold">{model.name}</h3>
                      <Badge variant={getStatusColor(model.status)}>
                        {model.status}
                      </Badge>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">{model.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Brain className="h-4 w-4 mr-1" />
                        {model.type}
                      </span>
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {model.lastUsed}
                      </span>
                      <span className="flex items-center">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        {model.accuracy}% accuracy
                      </span>
                      <span className="flex items-center">
                        <Activity className="h-4 w-4 mr-1" />
                        {model.requestCount} requests
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedModel(model);
                        setShowDetailsModal(true);
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Model Details Modal */}
      <Modal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)}>
        <div className="p-6 max-w-2xl">
          <h2 className="text-xl font-bold mb-4">AI Model Details</h2>
          {selectedModel && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold">{selectedModel.name}</h3>
                  <p className="text-gray-600">{selectedModel.description}</p>
                </div>
                <Badge variant={getStatusColor(selectedModel.status)}>
                  {selectedModel.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Type:</strong> {selectedModel.type}
                </div>
                <div>
                  <strong>Version:</strong> {selectedModel.version}
                </div>
                <div>
                  <strong>Accuracy:</strong> {selectedModel.accuracy}%
                </div>
                <div>
                  <strong>Response Time:</strong> {selectedModel.avgResponseTime}ms
                </div>
                <div>
                  <strong>Last Used:</strong> {selectedModel.lastUsed}
                </div>
                <div>
                  <strong>Created:</strong> {new Date(selectedModel.createdAt).toLocaleDateString()}
                </div>
              </div>

              {selectedModel.metrics && (
                <div>
                  <h4 className="font-medium mb-2">Performance Metrics:</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>Total Requests: {selectedModel.metrics.totalRequests}</div>
                    <div>Successful Requests: {selectedModel.metrics.successfulRequests}</div>
                    <div>Failed Requests: {selectedModel.metrics.failedRequests}</div>
                    <div>Average Response Time: {selectedModel.metrics.avgResponseTime}ms</div>
                  </div>
                </div>
              )}

              {selectedModel.errors && selectedModel.errors.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Recent Errors:</h4>
                  <div className="space-y-2">
                    {selectedModel.errors.slice(0, 3).map((error, index) => (
                      <div key={index} className="bg-red-50 p-3 rounded-md">
                        <div className="text-sm text-red-800">
                          <strong>{error.type}:</strong> {error.message}
                        </div>
                        <div className="text-xs text-red-600 mt-1">
                          {new Date(error.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                  Close
                </Button>
                <Button>
                  <Settings className="h-4 w-4 mr-2" />
                  Configure Model
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AIAnalytics;

